/* ============================================================
   PRODUCTS  (/products-v3.9)  — SESAAT Studio v3.9
   ============================================================
   Menggantikan rupa /products (LibraryView) dalam bahasa reka
   bentuk v3.9. Dua kumpulan sama seperti asal:
     · System   — pustaka produk terkurasi, dikongsi semua user
     · Mine     — produk peribadi user itu sendiri

   DUA PAPARAN, satu senarai (arahan user 2026-08-10):
     · Grid    — kad 4 sebaris; turun sendiri 3 → 2 → 1 bila sempit
     · Jadual  — lajur ala Notion, sel metrik boleh terus ditaip

   Yang bertukar hanya BADAN senarai. Carian, tab System/Mine,
   kiraan ready/pending, bar pilihan dan peraturan PENDING adalah
   perkara yang SAMA dalam kedua-dua paparan — kalau ia dipecahkan
   jadi dua salinan, satu daripadanya pasti terlepas pembetulan.

   Lencana READY/PENDING membawa maksud yang SAMA seperti sistem
   sebenar: READY = data produk sudah ada (boleh terus generate),
   PENDING = belum discrape, kena lengkapkan di Extension dahulu.
   Jangan tukar makna dua perkataan ini — pelanggan sudah biasa
   dengannya di halaman lama.

   ⚠️ PROTOTAIP — data contoh tetap. Tiada Firestore, tiada
   kesan pada produk sebenar.
   ============================================================ */

import { useEffect, useMemo, useState } from 'react';
import {
    Search, Plus, Lock, Copy, Trash2, X, ExternalLink, Check,
    LayoutGrid, Rows3, Tag, MoreVertical,
    Sparkles, Shirt, Sofa, Car, Package, UserRound,
} from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, PageHeader, Segmented, Field, EmptyState, Card } from '../design/ui';
import { useT } from '../i18n';

/* Kumpulan produk — chip monokrom, bukan titik berwarna. Warna dalam
   v3.9 disimpan untuk status (ready/pending) sahaja; kumpulan yang
   berwarna-warni akan bersaing dengan lencana itu dalam baris yang
   sama. Lihat nota jenama dalam tokens.css. */
const KUMPULAN = [
    { key: 'win', nama: 'Win' },
    { key: 'viral', nama: 'Viral' },
];

/* Ikon ganti gambar produk yang belum ada. Bukan isian gradien
   berwarna: dalam paparan Grid gambar itu menjadi benda paling besar
   pada kad, dan warna tepu di situ akan dibuang semula sebaik gambar
   sebenar masuk — jadi ia menilai reka bentuk atas hiasan sementara. */
const IKON_KATEGORI = { Beauty: Sparkles, Fashion: Shirt, Home: Sofa, Automotive: Car };

const CONTOH = [
    { id: 'a1', scope: 'system', ready: true, sku: '1729535475451004628', name: 'PERFUME PARADISE PEN FOR WOMEN — Packaging Baru (10 ml)', kategori: 'Beauty', harga: 'RM 24.90', asal: 'RM 39.90', diskaun: '38%', kumpulan: 'win', gmv: 6137535, c7: 1284, c14: 2610 },
    { id: 'a2', scope: 'system', ready: true, sku: '1729480091223841902', name: 'Set Tudung Bawal Premium Cotton Voal', kategori: 'Fashion', harga: 'RM 39.00', asal: 'RM 55.00', diskaun: '29%', kumpulan: null, gmv: 902480, c7: 318, c14: 741 },
    { id: 'a3', scope: 'system', ready: false, sku: '1729478550118822943', name: 'Umpan Semut Ant Bait Roach Killer Gel 10g', kategori: 'Home', harga: 'RM 12.50', asal: null, diskaun: null, kumpulan: null, gmv: null, c7: null, c14: null },
    { id: 'a4', scope: 'mine', ready: true, sku: '1729479113004472281', name: 'Minyak Wangi Attar Musk Al-Haramain 12ml', kategori: 'Beauty', harga: 'RM 18.00', asal: 'RM 25.00', diskaun: '28%', kumpulan: 'viral', gmv: 1450900, c7: 502, c14: 1188 },
    { id: 'a5', scope: 'mine', ready: false, sku: '1729477221950113374', name: 'DRY Pro Baju Hujan Motorcycle Rainsuit Coat', kategori: 'Automotive', harga: 'RM 55.00', asal: null, diskaun: null, kumpulan: null, gmv: null, c7: null, c14: null },
    { id: 'a6', scope: 'mine', ready: true, sku: '1729476003881224165', name: 'Official Glow Skin Toner Hydrating Brightening 100ml', kategori: 'Beauty', harga: 'RM 29.90', asal: 'RM 42.00', diskaun: '29%', kumpulan: 'win', gmv: 318220, c7: 96, c14: 205 },
    { id: 'a7', scope: 'system', ready: true, sku: '1729534118002937744', name: 'Rak Dapur Serbaguna 3 Tingkat Stainless Steel', kategori: 'Home', harga: 'RM 49.90', asal: 'RM 79.00', diskaun: '37%', kumpulan: null, gmv: 2204100, c7: 640, c14: 1502 },
    { id: 'a8', scope: 'system', ready: true, sku: '1729533902117446621', name: 'Cover Seat Kereta Universal Kulit PU Waterproof', kategori: 'Automotive', harga: 'RM 89.00', asal: 'RM 129.00', diskaun: '31%', kumpulan: 'viral', gmv: 1873400, c7: 421, c14: 986 },
    { id: 'a9', scope: 'system', ready: false, sku: '1729532774051339208', name: 'Baju Kurung Moden Sedondon Ibu & Anak', kategori: 'Fashion', harga: 'RM 79.00', asal: null, diskaun: null, kumpulan: null, gmv: null, c7: null, c14: null },
    { id: 'b1', scope: 'mine', ready: true, sku: '1729531660093882417', name: 'Serum Vitamin C Brightening Anti-Aging 30ml', kategori: 'Beauty', harga: 'RM 35.00', asal: 'RM 59.00', diskaun: '41%', kumpulan: null, gmv: 764300, c7: 233, c14: 517 },
    { id: 'b2', scope: 'mine', ready: true, sku: '1729530548829105336', name: 'Lampu LED Solar Outdoor Kalis Air 120 Mata', kategori: 'Home', harga: 'RM 27.90', asal: 'RM 45.00', diskaun: '38%', kumpulan: null, gmv: 1102750, c7: 358, c14: 802 },
];

/* Slot profil TikTok — 3 slot DEFAULT ikut lesen (1 lesen = 3-5 akaun
   TikTok); kotak bertitik di kiri = tambah slot baharu. Profil 1 memakai
   kiraan sebenar senarai contoh Mine; selebihnya statik/kosong. Konsep
   penuh: PLAN_IMPORT_BACKEND.md (sync showcase per akaun TikTok). */
const PROFIL = [
    { id: 'p1', nama: 'TT Utama', username: '@ttutama.my', inisial: 'U' },
    { id: 'p2', nama: 'TT Fesyen', username: '@ttfesyen.co', inisial: 'F', statik: { produk: 41, group: 2, sedia: 30, tunggu: 11 } },
    { id: 'p3', kosong: true },
];

/* Paparan pilihan diingat per-browser. Pengguna yang selesa dengan
   jadual tidak sepatutnya kena tukar semula setiap kali buka halaman. */
const KUNCI_PAPARAN = 'sesaat_v39_products_paparan';

const bacaPaparan = () => {
    try {
        const v = localStorage.getItem(KUNCI_PAPARAN);
        return v === 'jadual' || v === 'grid' ? v : 'grid';
    } catch { return 'grid'; }
};

const kosong = (v) => v === null || v === undefined || v === '';
const fmtNo = (n) => Number(n).toLocaleString('en-MY', { maximumFractionDigits: 0 });

export default function ProductsV39View({ onNavigate }) {
    const t = useT();
    const [scope, setScope] = useState('system');
    const [cari, setCari] = useState('');
    const [dipilih, setDipilih] = useState(() => new Set());
    const [paparan, setPaparan] = useState(bacaPaparan);
    const [profilAktif, setProfilAktif] = useState('p1');

    /* Kumpulan + metrik hidup dalam state halaman, bukan dalam CONTOH —
       data contoh kekal tetap supaya muat semula memulangkan keadaan asal. */
    const [kumpulanUbah, setKumpulanUbah] = useState({});   // id → key | null
    const [metrik, setMetrik] = useState({});               // id → { gmv, c7, c14 }
    const [menu, setMenu] = useState(null);                 // { jenis, id, x, y }

    useEffect(() => {
        try { localStorage.setItem(KUNCI_PAPARAN, paparan); } catch { /* mod privasi */ }
    }, [paparan]);

    /* Menu terapung berkedudukan TETAP, jadi ia tidak ikut kandungan yang
       menatal di bawahnya. Tutup sahaja apabila apa-apa menatal —
       `capture` wajib kerana yang menatal ialah panel, bukan tetingkap. */
    useEffect(() => {
        if (!menu) return undefined;
        const tutup = () => setMenu(null);
        window.addEventListener('scroll', tutup, true);
        window.addEventListener('resize', tutup);
        return () => {
            window.removeEventListener('scroll', tutup, true);
            window.removeEventListener('resize', tutup);
        };
    }, [menu]);

    const ditapis = useMemo(() => {
        const q = cari.trim().toLowerCase();
        return CONTOH
            .filter((p) => p.scope === scope)
            .filter((p) => !q || p.name.toLowerCase().includes(q) || p.sku.includes(q));
    }, [scope, cari]);

    const kiraSedia = ditapis.filter((p) => p.ready).length;
    const kiraTunggu = ditapis.length - kiraSedia;

    /* Kiraan kotak profil — dari SENARAI PENUH Mine, bukan hasil carian:
       kotak itu memperihalkan profil TikTok, bukan penapis semasa. */
    const profilSemua = CONTOH.filter((p) => p.scope === 'mine');
    const profilSedia = profilSemua.filter((p) => p.ready).length;
    const profilTunggu = profilSemua.length - profilSedia;

    /* Produk PENDING tidak boleh ditanda — ia tiada data untuk
       digenerate. Peraturan sama seperti modal Add Products. */
    const toggle = (p) => {
        if (!p.ready) return;
        setDipilih((prev) => {
            const next = new Set(prev);
            next.has(p.id) ? next.delete(p.id) : next.add(p.id);
            return next;
        });
    };

    const bolehPilih = ditapis.filter((p) => p.ready);
    const semuaDitanda = bolehPilih.length > 0 && bolehPilih.every((p) => dipilih.has(p.id));

    const toggleSemua = () => {
        setDipilih((prev) => {
            const next = new Set(prev);
            if (semuaDitanda) bolehPilih.forEach((p) => next.delete(p.id));
            else bolehPilih.forEach((p) => next.add(p.id));
            return next;
        });
    };

    const kiraan = ditapis.filter((p) => dipilih.has(p.id)).length;

    /* ---- Kumpulan ---- */
    const kumpulanKey = (p) => (p.id in kumpulanUbah ? kumpulanUbah[p.id] : p.kumpulan);
    const kumpulanNama = (key) => KUMPULAN.find((g) => g.key === key)?.nama || null;
    const setKumpulan = (id, key) => {
        setKumpulanUbah((prev) => ({ ...prev, [id]: key }));
        setMenu(null);
    };

    /* ---- Metrik ---- */
    const nilaiMetrik = (p, medan) => (metrik[p.id]?.[medan] !== undefined ? metrik[p.id][medan] : p[medan]);
    const simpanMetrik = (id, medan, nilai) =>
        setMetrik((prev) => ({ ...prev, [id]: { ...prev[id], [medan]: nilai } }));

    /* ---- Menu terapung ---- */
    const bukaMenu = (e, jenis, id) => {
        e.stopPropagation();
        const r = e.currentTarget.getBoundingClientRect();
        setMenu({
            jenis, id,
            x: Math.max(8, Math.min(r.left, window.innerWidth - 208)),
            y: r.bottom + 4,
        });
    };

    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>

            <PageHeader
                title={t('products.title')}
                subtitle={t('products.subtitle')}
                actions={
                    <Btn icon={<Plus size={15} strokeWidth={2} />} onClick={() => onNavigate?.('/extension-v3.9')}>
                        {t('products.import')}
                    </Btn>
                }
            />

            {/* Tab System/Mine berpindah dari kepala halaman ke KIRI kotak
                carian (arahan user 2026-08-16) — ia penapis senarai, jadi ia
                duduk bersama kawalan penapis lain dalam baris yang sama. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                <Segmented
                    value={scope}
                    onChange={(v) => { setScope(v); setDipilih(new Set()); }}
                    options={[
                        { value: 'system', label: t('products.scope.system') },
                        { value: 'mine', label: t('products.scope.mine') },
                    ]}
                />
                <Field
                    icon={<Search size={15} strokeWidth={1.9} />}
                    placeholder={t('products.searchPlaceholder')}
                    value={cari}
                    onChange={(e) => setCari(e.target.value)}
                    style={{ maxWidth: 320 }}
                />
                <Badge tone="ok">{t('products.countReady', { n: kiraSedia })}</Badge>
                {kiraTunggu > 0 && <Badge tone="neutral">{t('products.countPending', { n: kiraTunggu })}</Badge>}

                {/* Suis paparan — ikon sahaja, sebelah kanan bar carian.
                    Ia tetapan pandangan, bukan penapis: sengaja tidak
                    diletak bersama tab System/Mine di kepala halaman. */}
                <div className="s-viewseg" style={{ marginLeft: 'auto' }}>
                    <button
                        type="button"
                        aria-label={t('products.view.grid')} title={t('products.view.grid')}
                        aria-pressed={paparan === 'grid'}
                        className={`s-viewseg__b${paparan === 'grid' ? ' s-viewseg__b--on' : ''}`}
                        onClick={() => setPaparan('grid')}
                    >
                        <LayoutGrid size={15} strokeWidth={1.9} />
                    </button>
                    <button
                        type="button"
                        aria-label={t('products.view.table')} title={t('products.view.table')}
                        aria-pressed={paparan === 'jadual'}
                        className={`s-viewseg__b${paparan === 'jadual' ? ' s-viewseg__b--on' : ''}`}
                        onClick={() => setPaparan('jadual')}
                    >
                        <Rows3 size={15} strokeWidth={1.9} />
                    </button>
                </div>
                <span className="s-dim">{t('products.prototypeNote')}</span>
            </div>

            {/* ---- Baris profil TikTok (Mine sahaja, kedua-dua paparan) -------
                1 profile = 1 akaun TikTok user. Duduk DI BAWAH baris carian
                (arahan user 2026-08-16) supaya ia kekal kelihatan dalam
                paparan Grid mahupun Jadual. Tiada butang "Switch" — kotak itu
                sendiri ialah suis: klik = jadi aktif (bingkai --s-select,
                corak sama kad produk dipilih). Kotak bertitik di KIRI sekali
                = tambah profile baharu (3 slot default ikut lesen). */}
            {scope === 'mine' && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', flexWrap: 'wrap', marginBottom: 18 }}>
                    <button
                        type="button"
                        title={t('products.profile.add')}
                        style={{
                            flex: '0 0 92px', minHeight: 104, cursor: 'pointer',
                            border: '1.5px dashed var(--s-line-strong)', borderRadius: 'var(--s-r)',
                            background: 'none', fontFamily: 'inherit', color: 'var(--s-ink-3)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', gap: 6, padding: '10px 8px',
                        }}
                    >
                        <Plus size={18} strokeWidth={1.9} />
                        <span style={{ fontSize: 12, lineHeight: 1.3, textAlign: 'center' }}>{t('products.profile.add')}</span>
                    </button>

                    {PROFIL.map((pr) => (
                        <KadProfil
                            key={pr.id}
                            pr={pr}
                            aktif={profilAktif === pr.id}
                            stats={pr.kosong ? null : (pr.statik || {
                                produk: profilSemua.length, group: KUMPULAN.length,
                                sedia: profilSedia, tunggu: profilTunggu,
                            })}
                            onPilih={() => setProfilAktif(pr.id)}
                        />
                    ))}
                </div>
            )}

            {kiraan > 0 && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    padding: '9px 12px', marginBottom: 12,
                    background: 'var(--s-card)', border: '1px solid var(--s-line)', borderRadius: 'var(--s-r)',
                }}>
                    {/* Satu kunci penuh (bukan nombor + perkataan berasingan) —
                        susunan perkataan berbeza antara bahasa. */}
                    <span style={{ fontSize: 13, color: 'var(--s-ink)' }}>
                        {t('products.selectedCount', { n: kiraan })}
                    </span>
                    <span style={{ width: 1, height: 14, background: 'var(--s-line-strong)' }} />
                    <QuietBtn icon={<Plus size={13} strokeWidth={1.9} />}>{t('products.addToWorkspace')}</QuietBtn>
                    <QuietBtn icon={<Copy size={13} strokeWidth={1.9} />}>{t('products.copyToMine')}</QuietBtn>
                    <QuietBtn icon={<Trash2 size={13} strokeWidth={1.9} />} danger>{t('products.delete')}</QuietBtn>
                    <button
                        type="button"
                        onClick={() => setDipilih(new Set())}
                        aria-label={t('products.clearSelection')}
                        title={t('products.clearSelection')}
                        style={{
                            marginLeft: 'auto', width: 24, height: 24, padding: 0, cursor: 'pointer',
                            background: 'none', border: 0, display: 'grid', placeItems: 'center', color: 'var(--s-ink-3)',
                        }}
                    >
                        <X size={14} strokeWidth={2} />
                    </button>
                </div>
            )}

            {kiraTunggu > 0 && (
                <p className="s-meta" style={{ marginBottom: 12, fontSize: 13 }}>
                    {t('products.pendingNote')}
                </p>
            )}

            {ditapis.length === 0 ? (
                <EmptyState title={t('products.emptyTitle')}>
                    {cari.trim() ? t('products.emptySearch') : t('products.emptyImport')}
                </EmptyState>
            ) : paparan === 'grid' ? (
                <div className="s-pgrid">
                    {ditapis.map((p) => (
                        <KadProduk
                            key={p.id}
                            p={p}
                            on={dipilih.has(p.id)}
                            kumpulan={kumpulanNama(kumpulanKey(p))}
                            onToggle={() => toggle(p)}
                            onKumpulan={(e) => bukaMenu(e, 'kumpulan', p.id)}
                        />
                    ))}
                </div>
            ) : (
                <div className="s-tblwrap">
                    <table className="s-table s-table--rapat" style={{ minWidth: 1120 }}>
                        <thead>
                            <tr>
                                <th style={{ width: 42 }}>
                                    <Tick on={semuaDitanda} onClick={toggleSemua} label={t('products.selectAll')} />
                                </th>
                                <th style={{ width: 154 }}>{t('products.col.id')}</th>
                                <th style={{ width: 52 }} />
                                <th style={{ minWidth: 240 }}>{t('products.col.product')}</th>
                                <th style={{ width: 96 }}>{t('common.status')}</th>
                                <th style={{ width: 122 }}>{t('products.col.group')}</th>
                                <th className="s-td-num" style={{ width: 116 }}>{t('products.col.gmv')}</th>
                                <th className="s-td-num" style={{ width: 96 }}>{t('products.col.creator7')}</th>
                                <th className="s-td-num" style={{ width: 100 }}>{t('products.col.creator14')}</th>
                                <th className="s-td-num" style={{ width: 92 }}>{t('products.col.price')}</th>
                                <th className="s-td-num" style={{ width: 92 }}>{t('products.col.original')}</th>
                                <th className="s-td-num" style={{ width: 88 }}>{t('products.col.discount')}</th>
                                <th style={{ width: 44 }} />
                                <th style={{ width: 44 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {ditapis.map((p) => {
                                const on = dipilih.has(p.id);
                                const Ikon = IKON_KATEGORI[p.kategori] || Package;
                                const gNama = kumpulanNama(kumpulanKey(p));
                                return (
                                    <tr
                                        key={p.id}
                                        onClick={() => toggle(p)}
                                        className={on ? 's-trow s-trow--on' : 's-trow'}
                                        style={{ cursor: p.ready ? 'pointer' : 'not-allowed', opacity: p.ready ? 1 : 0.5 }}
                                    >
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <Tick
                                                on={on}
                                                locked={!p.ready}
                                                onClick={() => toggle(p)}
                                                label={p.ready ? t('products.selectProduct') : t('products.pendingLock')}
                                            />
                                        </td>

                                        <td className="s-num" style={{ whiteSpace: 'nowrap' }}>{p.sku}</td>

                                        <td>
                                            <span className="s-thumb s-thumb--kecil"><Ikon size={15} strokeWidth={1.6} /></span>
                                        </td>

                                        <td>
                                            <div className="s-td-strong s-clamp2" title={p.name}>{p.name}</div>
                                            <div style={{ fontSize: 12, color: 'var(--s-ink-3)', marginTop: 2 }}>{p.kategori}</div>
                                        </td>

                                        <td>{p.ready ? <Badge tone="ok">READY</Badge> : <Badge tone="neutral">PENDING</Badge>}</td>

                                        <td onClick={(e) => e.stopPropagation()}>
                                            <button
                                                type="button"
                                                className={`s-gchip${gNama ? '' : ' s-gchip--kosong'}`}
                                                onClick={(e) => bukaMenu(e, 'kumpulan', p.id)}
                                                title={t('products.changeGroup')}
                                            >
                                                <Tag size={11} strokeWidth={1.9} />
                                                {gNama || t('products.setGroup')}
                                            </button>
                                        </td>

                                        {[
                                            ['gmv', t('products.col.gmvShort')],
                                            ['c7', t('products.col.creator7')],
                                            ['c14', t('products.col.creator14')],
                                        ].map(([medan, label]) => (
                                            <td key={medan} className="s-td-num" onClick={(e) => e.stopPropagation()}>
                                                <SelAngka
                                                    nilai={nilaiMetrik(p, medan)}
                                                    label={label}
                                                    onSimpan={(v) => simpanMetrik(p.id, medan, v)}
                                                />
                                            </td>
                                        ))}

                                        <td className="s-td-num s-td-strong">{p.harga}</td>
                                        <td className="s-td-num" style={{ textDecoration: p.asal ? 'line-through' : 'none', color: 'var(--s-ink-3)' }}>
                                            {p.asal || '—'}
                                        </td>
                                        <td className="s-td-num">{p.diskaun ? `-${p.diskaun}` : '—'}</td>

                                        <td onClick={(e) => e.stopPropagation()}>
                                            <a
                                                className="s-tbtn"
                                                href="https://shop.tiktok.com"
                                                target="_blank"
                                                rel="noreferrer"
                                                title={t('products.openInTikTok')}
                                                aria-label={t('products.openInTikTok')}
                                            >
                                                <ExternalLink size={14} strokeWidth={1.9} />
                                            </a>
                                        </td>

                                        <td onClick={(e) => e.stopPropagation()}>
                                            <button
                                                type="button"
                                                className="s-tbtn"
                                                onClick={(e) => bukaMenu(e, 'tindakan', p.id)}
                                                title={t('products.actions')}
                                                aria-label={t('products.actions')}
                                            >
                                                <MoreVertical size={15} strokeWidth={1.9} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ---- Menu terapung: kumpulan / tindakan ---- */}
            {menu && (
                <>
                    <div
                        onClick={() => setMenu(null)}
                        style={{ position: 'fixed', inset: 0, zIndex: 205 }}
                    />
                    <div className="s-menu" style={{ left: menu.x, top: menu.y }}>
                        {menu.jenis === 'kumpulan' ? (
                            <>
                                <button type="button" className="s-menu__item" onClick={() => setKumpulan(menu.id, null)}>
                                    <Tag size={13} strokeWidth={1.8} style={{ opacity: .5 }} />
                                    {t('products.noGroup')}
                                </button>
                                {KUMPULAN.map((g) => {
                                    const p = CONTOH.find((x) => x.id === menu.id);
                                    const aktif = p && kumpulanKey(p) === g.key;
                                    return (
                                        <button
                                            key={g.key}
                                            type="button"
                                            className={`s-menu__item${aktif ? ' s-menu__item--on' : ''}`}
                                            onClick={() => setKumpulan(menu.id, g.key)}
                                        >
                                            <Tag size={13} strokeWidth={1.8} />
                                            <span style={{ flex: 1 }}>{g.nama}</span>
                                            {aktif && <Check size={13} strokeWidth={2.2} />}
                                        </button>
                                    );
                                })}
                            </>
                        ) : (
                            <>
                                <button type="button" className="s-menu__item" onClick={() => setMenu(null)}>
                                    <Plus size={13} strokeWidth={1.9} />{t('products.addToWorkspace')}
                                </button>
                                <button type="button" className="s-menu__item" onClick={() => setMenu(null)}>
                                    <Copy size={13} strokeWidth={1.9} />{t('products.copyToMine')}
                                </button>
                                <button type="button" className="s-menu__item s-menu__item--stop" onClick={() => setMenu(null)}>
                                    <Trash2 size={13} strokeWidth={1.9} />{t('products.delete')}
                                </button>
                            </>
                        )}
                    </div>
                </>
            )}

        </div>
    );
}

/* ---------- Kad produk (paparan Grid) --------------------------------
   Gambar di atas, butiran di bawah — bentuk yang sama dengan kad
   Marketplace supaya dua halaman itu tidak nampak datang dari app yang
   berlainan. Seluruh kad ialah kawasan pilih; hanya chip kumpulan yang
   menahan klik itu daripada merebak. */
function KadProduk({ p, on, kumpulan, onToggle, onKumpulan }) {
    const t = useT();
    const Ikon = IKON_KATEGORI[p.kategori] || Package;

    return (
        <Card
            hover={p.ready}
            onClick={onToggle}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
            role="button"
            tabIndex={0}
            aria-pressed={on}
            className={on ? 's-pcard s-pcard--on' : 's-pcard'}
            style={{ cursor: p.ready ? 'pointer' : 'not-allowed', opacity: p.ready ? 1 : 0.55 }}
        >
            <div className="s-thumb s-thumb--besar">
                <Ikon size={26} strokeWidth={1.4} />

                <span style={{ position: 'absolute', top: 8, left: 8 }}>
                    <Tick on={on} locked={!p.ready} label={p.ready ? t('products.selectProduct') : t('products.pendingLock')} />
                </span>
                <span style={{ position: 'absolute', top: 8, right: 8 }}>
                    {p.ready ? <Badge tone="ok">READY</Badge> : <Badge tone="neutral">PENDING</Badge>}
                </span>
            </div>

            <div style={{ padding: '11px 13px 13px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div className="s-num" style={{ lineHeight: 1.3 }}>{p.sku}</div>

                {/* Dua baris tetap — nama produk TikTok panjang berbeza-beza,
                    dan tanpa had ini tinggi kad dalam grid jadi tak sekata. */}
                <div className="s-clamp2" style={{
                    fontSize: 13.5, fontWeight: 600, color: 'var(--s-ink)',
                    lineHeight: 1.35, minHeight: 36,
                }} title={p.name}>{p.name}</div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: 'var(--s-ink)' }}>{p.harga}</span>
                    {p.asal && (
                        <span style={{ fontSize: 12, color: 'var(--s-ink-3)', textDecoration: 'line-through' }}>{p.asal}</span>
                    )}
                    <span style={{ fontSize: 12, color: 'var(--s-ink-3)', marginLeft: 'auto' }}>{p.kategori}</span>
                </div>

                <button
                    type="button"
                    className={`s-gchip${kumpulan ? '' : ' s-gchip--kosong'}`}
                    onClick={onKumpulan}
                    title={t('products.changeGroup')}
                    style={{ alignSelf: 'flex-start' }}
                >
                    <Tag size={11} strokeWidth={1.9} />
                    {kumpulan || t('products.setGroup')}
                </button>
            </div>
        </Card>
    );
}

/* ---------- Kad profil TikTok ------------------------------------------
   Satu kad = satu akaun TikTok. Kad aktif memakai bingkai --s-select —
   corak SAMA dengan kad produk dipilih (s-pcard--on), bukan warna baharu.
   Slot kosong papar ikon orang + hint sync; ia tetap boleh diklik jadi
   aktif (kelak: profil aktif menapis senarai produk di bawahnya). */
function KadProfil({ pr, aktif, stats, onPilih }) {
    const t = useT();
    return (
        <Card
            hover
            onClick={onPilih}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPilih(); } }}
            role="button"
            tabIndex={0}
            aria-pressed={aktif}
            style={{
                flex: '1 1 180px', minWidth: 180, cursor: 'pointer',
                padding: '12px 14px',
                borderColor: aktif ? 'var(--s-select)' : undefined,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden style={{
                    width: 34, height: 34, flex: 'none', borderRadius: 10,
                    background: 'var(--s-fill)', border: '1px solid var(--s-line)',
                    display: 'grid', placeItems: 'center',
                    fontSize: 13, fontWeight: 700, color: pr.kosong ? 'var(--s-ink-3)' : 'var(--s-ink)',
                }}>
                    {pr.kosong ? <UserRound size={16} strokeWidth={1.9} /> : pr.inisial}
                </span>
                {aktif && <Badge tone="neutral" style={{ marginLeft: 'auto' }}>{t('products.profile.active')}</Badge>}
            </div>

            <div style={{ marginTop: 10, fontSize: 13.5, fontWeight: 600, color: 'var(--s-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {pr.kosong ? t('products.profile.slot', { n: 3 }) : pr.nama}
            </div>
            <div style={{ marginTop: 1, fontSize: 12, color: 'var(--s-ink-3)' }}>
                {pr.kosong ? t('products.profile.unused') : pr.username}
            </div>

            {pr.kosong ? (
                <div style={{ marginTop: 8 }}>
                    <span className="s-dim" style={{ fontSize: 12 }}>{t('products.profile.syncHint')}</span>
                </div>
            ) : (
                <>
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--s-ink-2)' }}>
                        {t('products.profile.products', { n: stats.produk })}
                        <span style={{ color: 'var(--s-ink-3)' }}> · </span>
                        {t('products.profile.groups', { n: stats.group })}
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <Badge tone="ok">{t('products.countReady', { n: stats.sedia })}</Badge>
                        {stats.tunggu > 0 && <Badge tone="neutral">{t('products.countPending', { n: stats.tunggu })}</Badge>}
                    </div>
                </>
            )}
        </Card>
    );
}

/* ---------- Kotak tanda ------------------------------------------------
   Bentuk yang sama di kedua-dua paparan. Produk PENDING memakai mangga,
   bukan kotak kosong — kotak kosong membaca sebagai "belum ditanda",
   sedangkan ia sebenarnya "tidak boleh ditanda". */
function Tick({ on, locked, onClick, label }) {
    const isi = {
        width: 16, height: 16, flex: 'none', borderRadius: 5,
        display: 'grid', placeItems: 'center',
        border: `1px solid ${on ? 'var(--s-select)' : 'var(--s-line-strong)'}`,
        background: on ? 'var(--s-select)' : 'var(--s-card)',
        color: on ? 'var(--s-select-ink)' : 'var(--s-ink-3)',
        padding: 0, cursor: onClick && !locked ? 'pointer' : 'default',
    };

    const isian = locked
        ? <Lock size={9} strokeWidth={2.2} />
        : on ? <Check size={11} strokeWidth={3} /> : null;

    if (!onClick) return <span style={isi} aria-label={label} title={label}>{isian}</span>;

    return (
        <button type="button" style={isi} onClick={onClick} aria-label={label} title={label} aria-pressed={!!on}>
            {isian}
        </button>
    );
}

/* ---------- Sel angka boleh taip (paparan Jadual) ----------------------
   Klik sel → taip → Enter. Ini yang membezakan jadual daripada senarai:
   angka research disunting di tempatnya, bukan melalui borang berasingan.

   ⚠️ PROTOTAIP — nilai hidup dalam state halaman sahaja. */
function SelAngka({ nilai, label, onSimpan }) {
    const t = useT();
    const [taip, setTaip] = useState(false);
    const [draf, setDraf] = useState('');

    const mula = () => { setDraf(kosong(nilai) ? '' : String(nilai)); setTaip(true); };

    const simpan = () => {
        const mentah = draf.trim().replace(/[,\s]/g, '');
        if (mentah === '') onSimpan(null);
        else {
            const n = Number(mentah);
            if (!Number.isNaN(n) && n >= 0) onSimpan(n);
        }
        setTaip(false);
    };

    if (taip) {
        return (
            <input
                autoFocus
                className="s-cellnum s-cellnum--taip"
                value={draf}
                inputMode="numeric"
                placeholder="0"
                aria-label={label}
                onChange={(e) => setDraf(e.target.value)}
                onBlur={simpan}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') simpan();
                    else if (e.key === 'Escape') setTaip(false);
                }}
            />
        );
    }

    return (
        <button
            type="button"
            className={`s-cellnum${kosong(nilai) ? ' s-cellnum--kosong' : ''}`}
            onClick={mula}
            title={t('products.cell.clickToType', { label })}
        >
            {kosong(nilai) ? '—' : fmtNo(nilai)}
        </button>
    );
}

/* Butang senyap dalam bar pilihan — bentuk sama di semua halaman v3.9. */
function QuietBtn({ icon, danger, children }) {
    return (
        <button
            type="button"
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: 0, background: 'none', border: 0, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 12.5,
                color: danger ? 'var(--s-stop)' : 'var(--s-ink-2)',
            }}
        >
            {icon}{children}
        </button>
    );
}
